import React, { useEffect, useRef, useState } from 'react';
import { useField, ErrorMessage } from 'formik';
import { loadGoogleMaps } from '../hooks/map';

interface AddressInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
	name: string;
	label?: string;
	placeholder?: string;
	size?: 'default' | 'sm';
	error?: string;
	onPlaceSelected?: (place: google.maps.places.PlaceResult) => void;
	onTimezoneOffsetFetched?: (offsetMinutes: number | null) => void;
	regionScope?: 'lagos' | 'nigeria' | 'international';
}

export const AddressInput = ({
	name,
	label,
	placeholder,
	size = 'default',
	error,
	onPlaceSelected,
	onTimezoneOffsetFetched,
	regionScope,
	...props
}: AddressInputProps) => {
	const [field, meta] = useField(name);
	const inputRef = useRef<HTMLInputElement>(null);
	const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const isActive = meta.touched && !meta.error;
	const hasError = meta.touched && meta.error;

	const sizeClasses = {
		default: 'h-12',
		sm: 'h-10',
	};

	const fetchTimezoneOffset = async (lat: number, lng: number) => {
		const timezoneApiKey = process.env.REACT_APP_GM_TIMEZONE_API_KEY;
		if (!timezoneApiKey) {
			console.error('Google Maps API key is not configured for timezone.');
			onTimezoneOffsetFetched?.(null);
			return;
		}

		const timestamp = Math.floor(Date.now() / 1000); // Current Unix timestamp

		try {
			const response = await fetch(
				`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${timezoneApiKey}`,
			);
			const data = await response.json();

			if (data.status === 'OK') {
				const offsetMinutes = (data.rawOffset + data.dstOffset) / 60;
				onTimezoneOffsetFetched?.(offsetMinutes);
			} else {
				console.error('Error fetching timezone:', data.status, data.errorMessage);
				onTimezoneOffsetFetched?.(null);
			}
		} catch (error) {
			console.error('Error fetching timezone:', error);
			onTimezoneOffsetFetched?.(null);
		}
	};

	// Initialize autocomplete
	const initAutocomplete = (region: 'lagos' | 'nigeria' | 'international' = 'nigeria') => {
		if (!inputRef.current || !window.google?.maps?.places) {
			setLoadError('Maps library not loaded');
			return;
		}

		if (autocompleteRef.current) {
			google.maps.event.clearInstanceListeners(autocompleteRef.current);
		}

		let componentRestrictions: google.maps.places.ComponentRestrictions | undefined;

		if (region === 'lagos' || region === 'nigeria') {
			componentRestrictions = { country: 'NG' };
		}
		const defaultBounds = {
			north: 7.5,
			south: 6.0,
			east: 4.5,
			west: 3.0,
		};
		autocompleteRef.current = new google.maps.places.Autocomplete(
			inputRef.current,

			{
				bounds: defaultBounds,
				fields: ['formatted_address', 'geometry', 'address_components', 'icon', 'name'],
				strictBounds: false,
				componentRestrictions,
			},
		);

		autocompleteRef.current.addListener('place_changed', () => {
			const place = autocompleteRef.current?.getPlace();

			if (place?.geometry?.location) {
				field.onChange({
					target: {
						name: field.name,
						value: place.formatted_address,
					},
				});

				onPlaceSelected?.(place);
				fetchTimezoneOffset(place.geometry.location.lat(), place.geometry.location.lng());
			}
		});
	};

	// Load Google Maps script
	useEffect(() => {
		const apiKey = process.env.REACT_APP_PUBLIC_GOOGLE_API_KEY;
		if (!apiKey) {
			setLoadError('Google Maps API key is not configured');
			return;
		}

		loadGoogleMaps(apiKey)
			.then(() => initAutocomplete(regionScope))
			.catch((error) => {
				console.error('Google Maps loading error:', error);
				setLoadError('Failed to load address suggestions');
			});

		return () => {
			// Cleanup
			if (autocompleteRef.current) {
				google.maps.event.clearInstanceListeners(autocompleteRef.current);
			}
		};
	}, [regionScope]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="flex flex-col gap-1 relative">
			{label && (
				<label htmlFor={name} className="text-sm text-greyscale-600">
					{label} <span className="text-alert-error-100">*</span>
				</label>
			)}
			<input
				ref={inputRef}
				name={field.name}
				id={name}
				placeholder={placeholder}
				onBlur={field.onBlur}
				value={field.value}
				onChange={field.onChange}
				autoComplete="off"
				className={`
          w-full rounded-lg border bg-white px-4 py-3 text-sm text-greyscale-900
          focus:outline-none ${sizeClasses[size]}
          ${!meta.touched && 'border-greyscale-100'}
          ${isActive ? `focus:border-[#FE7F20] focus:shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]` : ''}
          ${hasError ? `border-[#DF1C41] shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]` : ''}
        `}
				{...props}
			/>

			{loadError && (
				<div className="!flex !items-center !gap-1 !mt-1">
					<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
							stroke="#DF1C41"
							stroke-width="1.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>

					<p className="!text-xs !font-semibold !text-[#DF1C41]">{loadError}</p>
				</div>
			)}

			<ErrorMessage name={name}>
				{(msg) => (
					<div className="!flex !items-center !gap-1 !mt-1">
						<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
								stroke="#DF1C41"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>

						<p className="!text-xs !font-semibold !text-[#DF1C41]">{msg}</p>
					</div>
				)}
			</ErrorMessage>

			{error && !meta.error && (
				<div className="!flex !items-center !gap-1 !mt-1">
					<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
							stroke="#DF1C41"
							stroke-width="1.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>

					<p className="!text-xs !font-semibold !text-[#DF1C41]">{error}</p>
				</div>
			)}
		</div>
	);
};
